import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { ArrowRight, FunnelSimple } from "@phosphor-icons/react";

const KIND_STYLE = {
  leave: "bg-orange-50 text-orange-800 border-orange-200",
  regularization: "bg-blue-50 text-blue-800 border-blue-200",
  reimbursement: "bg-emerald-50 text-emerald-800 border-emerald-200",
  fnf: "bg-purple-50 text-purple-800 border-purple-200",
  timesheet: "bg-adb-navy/10 text-adb-navy border-adb-navy/30",
  feedback360: "bg-pink-50 text-pink-800 border-pink-200",
};

const KIND_LABEL = {
  leave: "Leave", regularization: "Regularization", reimbursement: "Reimbursement",
  fnf: "F&F", timesheet: "Timesheet", feedback360: "360° Feedback",
};

export default function ApprovalsInbox() {
  const { user, tenantId } = useAuth();
  const [data, setData] = useState({ total: 0, items: [] });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};

  useEffect(() => {
    setLoading(true);
    api.get("/approvals-inbox", { params }).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [tenantId]);

  const kinds = Object.keys(KIND_LABEL);
  const counts = kinds.reduce((a, k) => ({ ...a, [k]: data.items.filter((i) => i.kind === k).length }), {});
  const filtered = filter === "all" ? data.items : data.items.filter((i) => i.kind === filter);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-semibold">Command center</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Approvals inbox</h1>
        <p className="text-sm text-zinc-500 mt-2">Every pending item across leaves, regularization, reimbursements, F&amp;F, timesheets, and 360° feedback in one place.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="inbox-filter">
        <button onClick={() => setFilter("all")} className={`text-xs px-3 py-1.5 border rounded-sm flex items-center gap-1.5 transition-colors ${filter === "all" ? "bg-adb-navy text-white border-adb-navy" : "border-zinc-300 hover:border-adb-navy"}`} data-testid="inbox-filter-all">
          <FunnelSimple size={12} /> All <span className="font-mono-plex">({data.total})</span>
        </button>
        {kinds.map((k) => (
          <button key={k} onClick={() => setFilter(k)} className={`text-xs px-3 py-1.5 border rounded-sm transition-colors ${filter === k ? "bg-adb-navy text-white border-adb-navy" : "border-zinc-300 hover:border-adb-navy"}`} data-testid={`inbox-filter-${k}`}>
            {KIND_LABEL[k]} <span className="font-mono-plex">({counts[k] || 0})</span>
          </button>
        ))}
      </div>

      <div className="border border-zinc-200 bg-white rounded-sm" data-testid="inbox-list">
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-4xl">🎯</div>
            <div className="font-display text-lg font-bold mt-2">You're all caught up.</div>
            <div className="text-xs text-zinc-500">Nothing pending in {filter === "all" ? "the queue" : KIND_LABEL[filter]}.</div>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {filtered.map((r) => (
              <Link key={`${r.kind}-${r.id}`} to={r.route} className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors group" data-testid={`inbox-item-${r.id}`}>
                <Badge variant="outline" className={`rounded-sm ${KIND_STYLE[r.kind]} shrink-0`}>{KIND_LABEL[r.kind]}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.title}</div>
                  <div className="text-xs text-zinc-500 truncate mt-0.5">{r.sub} · raised by <span className="font-semibold text-adb-navy">{r.by}</span></div>
                  {r.stage && r.stage.total_stages > 1 && (
                    <div className="flex items-center gap-1.5 mt-1.5" data-testid={`inbox-stage-${r.id}`}>
                      <div className="flex gap-0.5">
                        {Array.from({ length: r.stage.total_stages }).map((_, si) => (
                          <div key={si} className={`w-3 h-1 rounded-full ${si < r.stage.stage_index ? "bg-adb-green" : si === r.stage.stage_index ? "bg-adb-navy" : "bg-zinc-200"}`}></div>
                        ))}
                      </div>
                      <span className="text-[10px] text-zinc-500">
                        Stage {r.stage.stage_index + 1} of {r.stage.total_stages}
                        {r.stage.current_stage_label && <span className="ml-1 text-adb-navy font-semibold">· {r.stage.current_stage_label}</span>}
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-[10px] font-mono-plex text-zinc-400 hidden md:block shrink-0">{r.at ? new Date(r.at).toLocaleDateString() : "—"}</div>
                <ArrowRight size={16} className="text-zinc-400 group-hover:text-adb-navy group-hover:translate-x-0.5 transition-all shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
